import { useEffect, useState } from "react";
import { useOrders } from "./useHook";
import { useLocation } from "react-router-dom";

const Invoice = () => {
  const location = useLocation();
  const { orderId, autoPrint } = location.state || {};
  const { getOrderByOrderId } = useOrders();
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    if (orderId) {
      getOrderByOrderId(orderId, setOrderData);
    }
  }, [orderId]);

  // Auto-print when orderData is loaded and autoPrint flag is true
  useEffect(() => {
    if (autoPrint && orderData) {
      // Small delay to ensure the page is fully rendered
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoPrint, orderData]);

  const products = orderData?.productInfo || [];

  // Use the new response structure fields
  // The API now provides: originalPrice, discountedPrice, discountPercentage, itemDiscountedTotal
  const totalAmount = orderData?.totalAmount
    ? parseFloat(orderData.totalAmount)
    : products.reduce(
      (sum, product) => sum + (parseFloat(product.itemDiscountedTotal) || 0),
      0
    );

  const createdAt = new Date(orderData?.createdAt);
  const formattedDate = createdAt
    .toLocaleDateString("en-GB")
    .replace(/\//g, "-");
  const formattedTime = createdAt.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <style>{`
        .invoice-container {
          overflow-x: hidden;
        }
        .invoice-table-wrapper {
          overflow-x: auto;
        }
        @media print {
          @page {
            margin: 15mm;
            size: A4;
          }
          body * {
            visibility: hidden;
          }
          .invoice-container, .invoice-container * {
            visibility: visible;
          }
          .invoice-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 0;
            margin: 0;
            background: white;
          }
          .invoice-container .bg-gray-50 {
            background: white !important;
          }
          .no-print {
            display: none !important;
          }
          /* Ensure table prints properly */
          table {
            page-break-inside: auto;
            width: 100%;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          /* Remove scrollbars in print */
          .overflow-x-auto {
            overflow: visible !important;
          }
          /* Ensure table fits on page */
          .invoice-container {
            overflow: visible !important;
          }
        }
      `}</style>
      <div className="invoice-container bg-gray-50 p-10 print:p-0 print:bg-white">
        <div className="bg-white p-8 shadow-md rounded-lg mx-auto print:shadow-none print:rounded-none">
          <div className="flex justify-between items-center border-b pb-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-green-700">INVOICE</h1>
              <p className="text-gray-500">{orderData?.orderId}</p>
            </div>
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 print:hidden"
              onClick={handlePrint}
            >
              Print
            </button>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-green-700">GharPlans</h1>
          </div>
          <div className="flex justify-between mb-6">
            <div>
              <h2 className="font-semibold text-lg">Bill From:</h2>
              <p className="text-gray-700">Address: 469 G1 Johar Town Lahore</p>
              <p className="text-gray-700">Phone: +92 339 5111199</p>
              <p className="text-gray-500">Email: info@gharplans.com</p>
            </div>
            <div>
              <h2 className="font-semibold text-lg">Bill To:</h2>
              <p className="text-gray-700">
                Name: {orderData?.user?.firstName} {orderData?.user?.lastName}
              </p>
              <p className="text-gray-500">Email: {orderData?.user?.email}</p>
              {orderData?.userBillingAddress && (
                <>
                  <p className="text-gray-700 mt-2">
                    Address: {orderData.userBillingAddress.streetAddress}
                  </p>
                  <p className="text-gray-700">
                    {orderData.userBillingAddress.city}, {orderData.userBillingAddress.postalCode}
                  </p>
                  {orderData.userBillingAddress.contactNo && (
                    <p className="text-gray-700">Phone: {orderData.userBillingAddress.contactNo}</p>
                  )}
                </>
              )}
            </div>
            <div>
              <h2 className="font-semibold text-lg">Date:</h2>
              <p className="text-gray-500">{formattedDate}</p>
              <p className="text-gray-500">{formattedTime}</p>
              <div className="mt-3">
                <p className="text-gray-700">
                  <span className="font-semibold">Payment Method:</span>{" "}
                  {orderData?.paymentType === "Make Transfer"
                    ? "Bank Transferred"
                    : orderData?.paymentType === "Pay Now"
                      ? "Credit/Debit Card"
                      : orderData?.paymentType || "N/A"}
                </p>
                <p className="text-gray-700">
                  <span className="font-semibold">Delivery Mode:</span> {orderData?.deliveryType?.deliveryType || "N/A"}
                </p>
                {orderData?.totalWeight && (
                  <p className="text-gray-700">
                    <span className="font-semibold">Weight:</span> {orderData.totalWeight} {orderData?.unit || "kg"}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="invoice-table-wrapper overflow-x-auto print:overflow-visible">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-4 py-2 text-left w-16">Sr. No</th>
                  <th className="border px-4 py-2 text-left w-2/5">Product Name</th>
                  <th className="border px-4 py-2 text-left w-28">Variant</th>
                  <th className="border px-4 py-2 text-left w-24">Color</th>
                  <th className="border px-4 py-2 text-left w-20">Quantity</th>
                  <th className="border px-4 py-2 text-left w-24">Price</th>
                  <th className="border px-4 py-2 text-left w-24">Discount</th>
                  <th className="border px-4 py-2 text-left w-24">Rate</th>
                  <th className="border px-4 py-2 text-left w-24">Total</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => {
                  // Use new response structure fields
                  const originalPrice = parseFloat(product.originalPrice) || 0;
                  const discountedPrice = parseFloat(product.discountedPrice) || originalPrice;
                  const discountPercentage = product.discountPercentage || 0;
                  const itemTotal = parseFloat(product.itemDiscountedTotal) || 0;
                  const variantData = orderData?.variantProductData?.[index];
                  const variantName = variantData?.variant?.name ?? "—";
                  const variantColor = variantData?.variantColor?.color ?? "—";

                  return (
                    <tr key={index}>
                      <td className="border px-4 py-2 w-16">{index + 1}</td>
                      <td className="border px-4 py-2 break-words">{product.productName}</td>
                      <td className="border px-4 py-2 break-words">{variantName}</td>
                      <td className="border px-4 py-2 break-words">{variantColor}</td>
                      <td className="border px-4 py-2 w-20 text-center">{product.quantity}</td>
                      <td className="border px-4 py-2 w-24 text-right">
                        {originalPrice.toFixed(2)}
                      </td>
                      <td className="border px-4 py-2 w-24 text-center">
                        {discountPercentage > 0 ? `${discountPercentage}%` : "0%"}
                      </td>
                      <td className="border px-4 py-2 w-24 text-right">
                        {discountedPrice.toFixed(2)}
                      </td>
                      <td className="border px-4 py-2 w-24 text-right">
                        {itemTotal.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end mt-6">
            <div className="w-full md:w-1/3">
              <div className="flex justify-between mb-2">
                <p className="text-gray-700">Sub Total:</p>
                <p className="text-gray-700 font-semibold">
                  Rs. {totalAmount.toFixed(2)}
                </p>
              </div>
              {orderData?.deliveryCharges && parseFloat(orderData.deliveryCharges) > 0 && (
                <div className="flex justify-between mb-2">
                  <p className="text-gray-700">Shipping Charges:</p>
                  <p className="text-gray-700 font-semibold">
                    Rs. {parseFloat(orderData.deliveryCharges).toFixed(2)}
                  </p>
                </div>
              )}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <p className="text-lg font-semibold text-green-700">Grand Total:</p>
                  <p className="text-2xl font-bold text-green-700">
                    Rs. {(totalAmount + (parseFloat(orderData?.deliveryCharges) || 0)).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Invoice;